/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React, { useEffect, useState } from "react";
import Customers from "../components/Customers";
import validateForm from "../../../common/utils/validateForm";
import { editCustomerDetails, getCustomerList } from "../../../api/UserServices";
import Constants from "../../../common/utils/Constants";
import UserData from "../../../common/security/UserData";
import { hasAccessRights } from "../../../common/security/PermissionUtils";
import Error from "../../404/containers/ErrorContainer";

const CustomersContainer = () => {
  const [customers, setCustomers] = useState({});
  const [collapsesOpen, setCollapsesOpen] = useState([]);
  const [hasData, setHasData] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [searchResult, setSearchResult] = useState();
  const [isLoading, setLoading] = useState(false);
  const [editCustomerInProgress, setEditCustomerInProgress] = useState(false);

  const [customer, setCustomer] = useState({
    id: "",
    accountNo: "",
    accountName: "",
    invoiceAccountNo: "",
    classification: "",
    mozcomUserName: "",
    mozcomUserEmail: "",
    srName: "",
    srEmail: "",
    srPhoneDialCode: "",
    srPhoneNo: "",
    csrName: "",
    csrEmail: "",
    csrPhoneDialCode: "",
    csrPhoneNo: "",
  });

  const [showActive, setShowActive] = useState(false);
  const [message, setMessage] = useState({
    type: null,
    message: "",
  });
  const accessibleCompaniesIds = JSON.parse(UserData.getAccessibleCompanies());
  const [companyId, setCompanyId] = useState(accessibleCompaniesIds && accessibleCompaniesIds.length > 0 ? accessibleCompaniesIds[0] : "");
  const [size, setSize] = useState(20);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const displaySize = [20, 50, 100];
  const { USER_PREFERENCES } = Constants;
  const { getUserPreference, setUserPreference } = UserData;
  const [companyIds, setCompanyIds] = useState([]);

  const getCustomerData = (newCompanyId, searchValue, newPage, limit) => {
    setLoading(true);
    getCustomerList(newCompanyId, searchValue, newPage, limit).then((response) => {
      setLoading(false);
      if (response && response.success && response.data) {
        const { data } = response;
        setCustomers(data);
        const parsedLimit = parseInt(limit, 10);
        setPage(data.totalCount < parsedLimit ? 0 : newPage);
        setSize(data.totalCount < parsedLimit ? 20 : parsedLimit);
        setCompanyId(newCompanyId);
        setSearchResult(data.totalCount);
        setHasData(true);
        setCollapsesOpen([]);
        setSearchTerm(searchValue);
        if (data.customerAccounts.length === 0) { setHasData(false); }
      } else {
        setHasData(false);
      }
    });
  };

  useEffect(() => {
    const limit = getUserPreference(USER_PREFERENCES.CUSTOMERS_TABLE_LIMIT) || 20;
    getCustomerData(companyId, searchTerm, page, limit);
    setCompanyIds(accessibleCompaniesIds);
  }, []);

  const toggleCollapses = (id) => {
    if (collapsesOpen.includes(id)) {
      const newCollapses = collapsesOpen.filter((each) => each !== id);
      setCollapsesOpen(newCollapses);
    } else {
      setCollapsesOpen([...collapsesOpen, id]);
    }
  };

  const toggleAll = () => {
    if (customers && customers.customerAccounts && Array.isArray(customers.customerAccounts)) {
      if (Array.isArray(collapsesOpen) && collapsesOpen.length > 0) {
        setCollapsesOpen([]);
      } else {
        setCollapsesOpen(customers.customerAccounts.map((each) => each.accountNo));
      }
    }
  };

  const getPageData = (newPage) => {
    getCustomerData(companyId, searchTerm, newPage, size);
  };

  const toggleEditPage = (customerData) => {
    const sr = customerData?.salesRep;
    const srPhone = sr?.phone;
    const csr = customerData?.csr;
    const csrPhone = csr?.phone;
    let mozcomUsers = [];
    let name = "";
    let email = "";
    mozcomUsers = customerData?.mozcomUsers;
    if (mozcomUsers?.length >= 1) {
      name = mozcomUsers[0].name;
      email = mozcomUsers[0].email;
    }
    setCustomer({
      ...customer,
      id: customerData?.id,
      accountNo: customerData?.accountNo,
      accountName: customerData?.accountName,
      invoiceAccountNo: customerData?.invoiceAccountNo,
      classification: customerData?.classification,
      mozcomUserName: name,
      mozcomUserEmail: email,
      srName: sr?.name,
      srEmail: sr?.email,
      srPhoneDialCode: srPhone?.dialCode,
      srPhoneNo: srPhone?.phoneNumber,
      csrName: csr?.name,
      csrEmail: csr?.email,
      csrPhoneDialCode: csrPhone?.dialCode,
      csrPhoneNo: csrPhone?.phoneNumber,
    });
    setShowActive(!showActive);
  };

  const resetCustomerData = () => {
    setCustomer({
      id: "",
      accountNo: "",
      accountName: "",
      invoiceAccountNo: "",
      classification: "",
      mozcomUserName: "",
      mozcomUserEmail: "",
      srName: "",
      srEmail: "",
      srPhoneDialCode: "",
      srPhoneNo: "",
      csrName: "",
      csrEmail: "",
      csrPhoneDialCode: "",
      csrPhoneNo: "",
    });
  };

  const methods = validateForm({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    validateCriteriaMode: "all",
  });

  const { clearErrors } = methods;

  const handleClose = () => {
    clearErrors([
      "classification",
      "mozcomUserName",
      "mozcomUserEmail",
      "srName",
      "srEmail",
      "srPhoneDialCode",
      "srPhoneNo",
      "csrName",
      "csrEmail",
      "csrPhoneDialCode",
      "csrPhoneNo"]);
    resetCustomerData();
    setShowActive(false);
  };

  const handleSave = () => {
    const updatedCustomerData = {};
    const salesRep = {
      name: customer.srName && customer.srName.trim(),
      email: customer.srEmail && customer.srEmail.trim(),
      phone: {
        dialCode: customer.srPhoneDialCode && customer.srPhoneDialCode.trim(),
        phoneNumber: customer.srPhoneNo && customer.srPhoneNo.trim(),
      },
    };
    const csr = {
      name: customer.csrName && customer.csrName.trim(),
      email: customer.csrEmail && customer.csrEmail.trim(),
      phone: {
        dialCode: customer.csrPhoneDialCode && customer.csrPhoneDialCode.trim(),
        phoneNumber: customer.csrPhoneNo && customer.csrPhoneNo.trim(),
      },
    };
    const mozcomUsers = [{
      name: customer.mozcomUserName && customer.mozcomUserName.trim(),
      email: customer.mozcomUserEmail && customer.mozcomUserEmail.trim(),
    }];
    updatedCustomerData.id = customer.id;
    updatedCustomerData.accountNo = customer.accountNo;
    updatedCustomerData.accountName = customer.accountName;
    updatedCustomerData.invoiceAccountNo = customer.invoiceAccountNo;
    updatedCustomerData.classification = customer.classification
      && customer.classification.toUpperCase();
    updatedCustomerData.salesRep = salesRep;
    updatedCustomerData.csr = csr;
    updatedCustomerData.mozcomUsers = mozcomUsers;
    setEditCustomerInProgress(true);
    editCustomerDetails(customer.id, updatedCustomerData).then((response) => {
      if (response && response.success && response.data) {
        setEditCustomerInProgress(false);
        handleClose();
        setMessage({ type: "success", message: response.messages[0] });
        resetCustomerData();
        getCustomerData(companyId, searchTerm, page, size);
        setTimeout(() => {
          setMessage({ type: null, message: "" });
        }, 3000);
      } else {
        setMessage({ type: "warning", message: "Something went wrong" });
        setEditCustomerInProgress(false);
        setTimeout(() => {
          setMessage({ type: null, message: "" });
          handleClose();
          resetCustomerData();
          getCustomerData(companyId, searchTerm, page, size);
        });
      }
    });
  };

  const handleEditCustomer = (event) => {
    const { value } = event.target;
    const key = event.target.name;
    clearErrors([key]);
    const newCustomer = { ...customer, [key]: value };
    setCustomer(newCustomer);
  };

  const handleSearchTerm = (event) => {
    const { value } = event.target;
    setSearchTerm(value);
  };

  const handleSearch = () => {
    getCustomerData(companyId, searchTerm.trim(), 0, size);
  };

  const handleKeyPress = (event) => {
    if (event.charCode === 13) {
      handleSearch();
    }
  };

  /**
  *This method is used to toogle dropdown
  *
  */
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  /**
  *This method is used to handle size dropdown
  *
  * @param {String} newSize
  */
  const handleSelectedSize = (newSize) => {
    setUserPreference(USER_PREFERENCES.CUSTOMERS_TABLE_LIMIT, newSize);
    getCustomerData(companyId, searchTerm, 0, newSize);
  };

  /**
  *This method is used to Select Country
  *
  * @param {String} selectedId
  */
  const handleSelectedCompany = (selectedId) => {
    getCustomerData(selectedId, searchTerm, 0, size);
  };

  return (
    <>
      { hasAccessRights(["ViewCustomersLink"])
        ? (
          <Customers
            customers={customers}
            collapsesOpen={collapsesOpen}
            toggleCollapses={toggleCollapses}
            hasData={hasData}
            toggleEditPage={toggleEditPage}
            showActive={showActive}
            handleClose={handleClose}
            handleSave={handleSave}
            handleEditCustomer={handleEditCustomer}
            customer={customer}
            searchTerm={searchTerm}
            handleSearchTerm={handleSearchTerm}
            handleSearch={handleSearch}
            page={page}
            getPageData={getPageData}
            size={size}
            totalCount={searchResult}
            isLoading={isLoading}
            methods={methods}
            toggleAll={toggleAll}
            handleKeyPress={handleKeyPress}
            message={message}
            editCustomerInProgress={editCustomerInProgress}
            displaySize={displaySize}
            toggleDropdown={toggleDropdown}
            dropdownOpen={dropdownOpen}
            handleSelectedSize={handleSelectedSize}
            handleSelectedCompany={handleSelectedCompany}
            companyId={companyId}
            companyIds={companyIds}
          />
        )
        : <Error />}
    </>
  );
};
export default CustomersContainer;
